import { put, select, takeLatest, all, takeEvery } from 'redux-saga/effects';
import { notify } from '@mrlannigan/reapop';
import { arrayUnshift, arrayPush, change } from 'redux-form';
import { sagaError } from 'helpers/handleGenericSagaError';
import { aliasIsValid } from 'helpers/validate';
import { push } from 'connected-react-router';
import { merge, get, set } from 'lodash';

import apiAuthCall from 'helpers/apiAuthCall';
import { throttle } from 'helpers/sagaEffects';
import {
    getDefaultDeliveryPlatform,
    isPlatformInChannel,
} from 'helpers/channels';
import { escapeSalesforceSoslSpecialCharacters } from 'helpers/utilities';
import { getCurrentUserScopes } from 'services/users/selectors';
import { getObjectiveShadow } from 'services/config/selectors';
import {
    updateThemeStyles,
    showGGBranding,
    hideGGBranding,
} from 'services/theme/duck';
import { CHANNEL_UPSELL_VIEW } from 'constants/capabilities';
import {
    fetchListSuccess,
    actions,
    createEntityForm,
    fetchMetaSuccess,
    fetchMetaSocialSuccess,
    fetchEntitySuccess,
    fetchEntityATVLabDataSuccess,
    fetchEntityChildrenSuccess,
    fetchEntityDataRangeSuccess,
    updateEntityBrandingForm,
    updateEntityForm,
    deleteEntitySuccess,
    setOpportunityIdInformation,
    searchForOpportunitiesSuccess,
} from './duck';
import {
    entitiesEndpoint,
    entityMetaEndpoint,
    entityMetaSocialEndpoint,
    updateEntityEndpoint,
    updateEntityBrandingEndpoint,
    updateEntityATVLabCSVEndpoint,
    getEntityATVLabDataEndpoint,
    getEntityEndpoint,
    getEntityChildrenEndpoint,
    getEntityDataRangeEndpoint,
    getEditFormFieldValue,
    getCreateFormFieldValue,
    getEntityDebugHierarchyEndpoint,
    getUnicornGraphQLEndpoint,
} from './selectors';

export function* createEntity(api, action) {
    const notificationId = 'createEntity';

    try {
        const payload = action.payload;

        payload.aliasIsValid = aliasIsValid(action);

        // Filter array, removing null form values--not platform specific
        ['EntityJobs', 'EntityOpportunities'].forEach((x) => {
            if (payload[x]) {
                payload[x] = payload[x].filter(Boolean);
            }
        });

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(entitiesEndpoint),
                data: payload,
            }
        );

        yield put(createEntityForm.success());
        yield put(
            notify({
                id: notificationId,
                message: `${action.payload.name || 'Entity'} created!`,
                status: result.status,
            })
        );
        yield put(push(`/admin/entities/${result.data.id}`));
    } catch (err) {
        yield put(createEntityForm.failure(err));
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* fetchEntityList(api, action) {
    const notificationId = 'getEntityList';

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(entitiesEndpoint),
            }
        );

        yield put(fetchListSuccess(result.data));
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* fetchEntityMeta(api, action) {
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(entityMetaEndpoint, action.payload),
            }
        );

        const { data } = result;

        const userScopes = yield select(getCurrentUserScopes);
        const objectiveShadow = yield select(getObjectiveShadow);
        const enableObjectiveSwitching = get(data, 'meta.enableObjectiveSwitching', false);
        // console.warn('Logic to apply override when present', { objectiveFromCookie, metaGlob: meta });
        if (objectiveShadow && enableObjectiveSwitching) {
            set(data, 'meta.programmaticObjective.global', objectiveShadow);
        }
        // if user doesn't have upsell scope don't allow upsell to be enabled
        if (
            userScopes.indexOf(CHANNEL_UPSELL_VIEW) === -1 &&
            data.upsellSettings.enabled
        ) {
            data.upsellSettings.enabledOrig = data.upsellSettings.enabled;
            data.upsellSettings.enabled = false;
        }

        const wlHideGGBranding = get(
            data,
            'meta.whitelabel.hideGGBranding',
            false
        );

        if (wlHideGGBranding) {
            yield put(hideGGBranding());
        } else {
            yield put(showGGBranding());
        }

        yield put(updateThemeStyles(data));
        yield put(fetchMetaSuccess(data));
    } catch (err) {
        yield put(
            notify({
                id: action.payload.transactionId,
                ...sagaError(err),
            })
        );
        yield put(push('/'));
    }
}

export function* fetchEntityMetaSocial(api, action) {
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(entityMetaSocialEndpoint, action.payload),
            }
        );

        const { data } = result;

        yield put(fetchMetaSocialSuccess(data));
    } catch (err) {
        yield put(
            notify({
                id: action.payload.transactionId,
                ...sagaError(err),
            })
        );
    }
}

export function* fetchEntityATVLabData(api, action) {
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(getEntityATVLabDataEndpoint, action.payload),
            }
        );

        const { data } = result;

        yield put(fetchEntityATVLabDataSuccess(data));
    } catch (err) {
        yield put(
            notify({
                id: action.payload.transactionId,
                ...sagaError(err),
            })
        );
    }
}

export function* fetchEntity(api, action) {
    const notificationId = action.type;

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(getEntityEndpoint, action.payload),
            }
        );

        yield put(fetchEntitySuccess(result.data));
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* updateEntity(api, action) {
    if (action.payload.version === 2) {
        delete action.payload.EntityJobs;
        delete action.payload.EntityOpportunities;
        delete action.payload.entityPlatform;
    } else {
        delete action.payload.workOrders;

        ['EntityJobs', 'EntityOpportunities'].forEach((x) => {
            if (action.payload[x]) {
                action.payload[x] = action.payload[x].filter(Boolean);
            }
        });
    }
    const notificationId = 'updateEntity';

    try {
        action.payload.aliasIsValid = aliasIsValid(action);

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                url: yield select(updateEntityEndpoint, {
                    entityId: action.payload.id,
                }),
                data: action.payload,
            }
        );

        yield put(updateEntityForm.success());
        yield put(
            notify({
                id: notificationId,
                message: `${action.payload.name || 'Entity'} updated!`,
                status: result.status,
            })
        );
        yield put(push(`/admin/entities/${action.payload.id}`));
    } catch (err) {
        yield put(updateEntityForm.failure(err));
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* updateEntityBranding(api, action) {
    const notificationId = 'updateEntityBranding';
    try {
        const formData = new FormData();
        const image = get(action, 'payload.branding.image[0]');
        if (image) {
            formData.append('image', image);
            formData.append('imageName', image.name);
        }

        ['removeLogo'].forEach((key) => {
            if (
                [undefined, null, ''].indexOf(action.payload.branding[key]) ===
                -1
            ) {
                formData.append(key, action.payload.branding[key]);
            }
        });

        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                headers: { 'content-type': 'multipart/form-data' },
                url: yield select(updateEntityBrandingEndpoint, {
                    entityId: action.payload.id,
                }),
                data: formData,
            }
        );

        yield put(updateEntityBrandingForm.success());
        yield put(
            notify({
                id: notificationId,
                message: `${action.payload.name} branding updated successfully`,
                status: result.status,
            })
        );
    } catch (err) {
        yield put(updateEntityBrandingForm.failure(err));
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* updateEntityATVLabCSV(api, action) {
    const notificationId = 'updateEntityATVLabCSV';
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'put',
                url: yield select(updateEntityATVLabCSVEndpoint, {
                    entityId: action.payload.entityId,
                }),
                data: action.payload.data,
            }
        );
        yield put(
            notify({
                id: notificationId,
                message: `${action.payload.name} ATV X-Channel Lift data updated successfully`,
                status: result.status,
            })
        );
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* deleteEntity(api, confirmMsg, action) {
    const notificationId = 'deleteEntity';

    if (!confirmMsg('Are you sure you wish to delete this entity?')) {
        // eslint-disable-line no-alert
        return;
    }

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'delete',
                url: yield select(updateEntityEndpoint, {
                    entityId: action.payload.id,
                }),
            }
        );

        yield put(
            notify({
                id: notificationId,
                message: `${action.payload.name || 'Entity'} deleted!`,
                status: result.status,
            })
        );
        yield put(deleteEntitySuccess());
        yield put(push('/admin/entities'));
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* fetchEntityChildren(api, action) {
    const notificationId = action.type;

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(getEntityChildrenEndpoint, action.payload),
            }
        );

        yield put(fetchEntityChildrenSuccess(result.data));
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* fetchEntityDataRange(api, action) {
    const notificationId = action.type;

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(getEntityDataRangeEndpoint, action.payload),
            }
        );

        yield put(fetchEntityDataRangeSuccess(result.data));
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* fetchHierarchyDump(api, action) {
    const notificationId = 'fetchHierarchyDump';

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(getEntityDebugHierarchyEndpoint, {
                    entityId: action.payload,
                }),
            }
        );

        yield put(
            notify({
                id: notificationId,
                message: `Entity ${action.payload} hierarchy dumped! Check network tab.`,
                status: result.status,
            })
        );
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export function* addNewWorkOrderToForm(formName, formSelector, action) {
    const baseWorkOrder = action.payload;
    const currentAvailableChannels = yield select(
        formSelector,
        'meta.channelConfig'
    );

    yield put(
        arrayUnshift(
            formName,
            'workOrders',
            merge(baseWorkOrder, {
                meta: {
                    channels: Object.keys(currentAvailableChannels).reduce(
                        (prev, channelName) => {
                            const defaultDeliveryPlatform = getDefaultDeliveryPlatform(
                                baseWorkOrder.adminPlatform,
                                channelName
                            );

                            prev[channelName] = [
                                {
                                    enabled:
                                        currentAvailableChannels[channelName]
                                            .enabled,
                                },
                            ];

                            if (defaultDeliveryPlatform) {
                                prev[
                                    channelName
                                ][0].deliveryPlatform = defaultDeliveryPlatform;
                            }

                            return prev;
                        },
                        {}
                    ),
                },
            })
        )
    );
}

export function* addNewCustomChannelToForm(formName, formSelector, action) {
    const baseCustomChannel = action.payload;
    yield put(
        arrayPush(
            formName,
            'meta.embeddedLinks',
            baseCustomChannel
        )
    );
}

export function* cloneExistingWorkOrderToForm(formName, formSelector, action) {
    const { baseWorkOrder, originalWorkOrder } = action.payload;
    const originalWorkOrderPlatform = originalWorkOrder.adminPlatform;
    const mergedWorkOrder = merge(baseWorkOrder, {
        salesforceId: originalWorkOrder.salesforceId ? originalWorkOrder.salesforceId : null,
        meta: {
            description: `COPY OF ${originalWorkOrderPlatform === 'salesforce' ? originalWorkOrder.salesforceId : originalWorkOrder.jobNumber}`,
            ...originalWorkOrder.meta
        },
    });

    yield put(
        arrayUnshift(
            formName,
            'workOrders',
            mergedWorkOrder
        )
    );
}

export function* resetWorkOrderToMatchBrand(formName, formSelector, action) {
    const { fieldKey } = action.payload;
    const currentAvailableChannels = yield select(
        formSelector,
        'meta.channelConfig'
    );

    const availableChannelKeys = Object.keys(currentAvailableChannels);
    /* eslint-disable no-restricted-syntax */
    for (const channelName of availableChannelKeys) {
        const currentValue = yield select(
            formSelector,
            `${fieldKey}.meta.channels.${channelName}[0].enabled`
        );

        if (currentValue !== currentAvailableChannels[channelName].enabled) {
            yield put(
                change(
                    formName,
                    `${fieldKey}.meta.channels.${channelName}[0].enabled`,
                    currentAvailableChannels[channelName].enabled
                )
            );
        }
    }
    /* eslint-enable no-restricted-syntax */
}

export function* switchAllWorkOrderPlatforms(formName, formSelector, action) {
    const { fieldKey, deliveryPlatform } = action.payload;
    const currentAvailableChannels = yield select(
        formSelector,
        'meta.channelConfig'
    );

    const availableChannelKeys = Object.keys(currentAvailableChannels);

    /* eslint-disable no-restricted-syntax */
    for (const channelName of availableChannelKeys) {
        if (isPlatformInChannel(channelName, deliveryPlatform)) {
            yield put(
                change(
                    formName,
                    `${fieldKey}.meta.channels.${channelName}[0].deliveryPlatform`,
                    deliveryPlatform
                )
            );
        }
    }
    /* eslint-enable no-restricted-syntax */
}

/**
 * @export
 * @param {*} api
 * @param {{ type: String, payload: String[] }} action
 */
export function* fetchOpportunityIdInformation(api, action) {
    const notificationId = 'fetchOpportunityIdInformation';

    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(getUnicornGraphQLEndpoint),
                data: {
                    query: `query getLineItems($salesforceOpportunityId: [String!]) {
                        lineItems(where: { salesforceOpportunityId_in: $salesforceOpportunityId }, first: 1) {
                            id,
                            salesforceOpportunityId,
                            advertiser {
                                salesforceAccountId
                            }
                            salesforceOpportunity {
                                name
                            }
                        }
                    }`,
                    variables: {
                        salesforceOpportunityId: action.payload,
                    },
                },
            }
        );

        for (const lineItem of result.data.data.lineItems) {
            yield put(
                setOpportunityIdInformation({
                    ...lineItem,
                    opportunityId: lineItem.salesforceOpportunityId,
                })
            );
        }
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

/**
 * @export
 * @param {*} api
 * @param {{ limit: Number, searchInput: String }} action
 */
export function* searchForOpportunityInfo(api, action) {
    const notificationId = 'searchForOpportunityInfo';
    const { limit, searchInput } = action.payload || {};
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(getUnicornGraphQLEndpoint),
                data: {
                    query: `query doSearch($limit:Int, $searchInput:String) {
                        salesforceOpportunities(
                            isSearch:true,
                            limit:$limit,
                            searchInput:$searchInput){
                            name,
                            id,
                            accountName,
                            createdDate
                        }
                    }`,
                    variables: {
                        limit,
                        searchInput: escapeSalesforceSoslSpecialCharacters(
                            searchInput
                        ),
                    },
                },
            }
        );

        // for (const lineItem of result.data.data.lineItems) {
        yield put(searchForOpportunitiesSuccess(result.data));
        // }
    } catch (err) {
        yield put(
            notify({
                id: notificationId,
                ...sagaError(err),
            })
        );
    }
}

export default function* entitySaga({ api }) {
    yield all([
        takeLatest(createEntityForm.REQUEST, createEntity, api),
        throttle(200, actions.FETCH_LIST, fetchEntityList, api),
        throttle(200, actions.FETCH_ENTITY_ATV_LAB_DATA, fetchEntityATVLabData, api),
        throttle(200, actions.FETCH_META, fetchEntityMeta, api),
        throttle(200, actions.FETCH_META_SOCIAL, fetchEntityMetaSocial, api),
        throttle(10000, actions.FETCH_HIERARCHY_DUMP, fetchHierarchyDump, api),
        takeLatest(actions.FETCH_ENTITY, fetchEntity, api),
        takeLatest(actions.DELETE_ENTITY, deleteEntity, api, window.confirm),
        takeLatest(updateEntityForm.REQUEST, updateEntity, api),
        takeLatest(updateEntityBrandingForm.REQUEST, updateEntityBranding, api),
        takeLatest(actions.UPDATE_ENTITY_ATV_LAB_CSV, updateEntityATVLabCSV, api),
        takeLatest(actions.FETCH_ENTITY_CHILDREN, fetchEntityChildren, api),
        takeLatest(actions.FETCH_DATA_RANGE, fetchEntityDataRange, api),
        takeEvery(
            actions.ADD_NEW_WORK_ORDER_TO_EDIT_FORM,
            addNewWorkOrderToForm,
            'editEntity',
            getEditFormFieldValue
        ),
        takeEvery(
            actions.ADD_NEW_WORK_ORDER_TO_CREATE_FORM,
            addNewWorkOrderToForm,
            'createEntity',
            getCreateFormFieldValue
        ),
        takeEvery(
            actions.ADD_NEW_CUSTOM_CHANNEL_TO_EDIT_FORM,
            addNewCustomChannelToForm,
            'editEntity',
            getEditFormFieldValue
        ),
        takeEvery(
            actions.ADD_NEW_CUSTOM_CHANNEL_TO_CREATE_FORM,
            addNewCustomChannelToForm,
            'createEntity',
            getCreateFormFieldValue
        ),
        takeEvery(
            actions.RESET_WORK_ORDER_TO_MATCH_BRAND_EDIT_FORM,
            resetWorkOrderToMatchBrand,
            'editEntity',
            getEditFormFieldValue
        ),
        takeEvery(
            actions.RESET_WORK_ORDER_TO_MATCH_BRAND_CREATE_FORM,
            resetWorkOrderToMatchBrand,
            'createEntity',
            getCreateFormFieldValue
        ),
        takeEvery(
            actions.CLONE_EXISTING_WORK_ORDER_TO_CREATE_FORM,
            cloneExistingWorkOrderToForm,
            'createEntity',
            getEditFormFieldValue
        ),
        takeEvery(
            actions.CLONE_EXISTING_WORK_ORDER_TO_EDIT_FORM,
            cloneExistingWorkOrderToForm,
            'editEntity',
            getEditFormFieldValue
        ),
        takeEvery(
            actions.SWITCH_ALL_WORK_ORDER_PLATFORMS_CREATE_FORM,
            switchAllWorkOrderPlatforms,
            'createEntity',
            getCreateFormFieldValue
        ),
        takeEvery(
            actions.SWITCH_ALL_WORK_ORDER_PLATFORMS_EDIT_FORM,
            switchAllWorkOrderPlatforms,
            'editEntity',
            getEditFormFieldValue
        ),
        takeEvery(
            actions.REQUEST_OPPORTUNITY_ID_INFORMATION,
            fetchOpportunityIdInformation,
            api
        ),
        throttle(
            500,
            actions.SEARCH_FOR_OPPORTUNITIES,
            searchForOpportunityInfo,
            api
        ),
    ]);
}



// WEBPACK FOOTER //
// ./src/services/entities/saga.js